import React, { useRef, useState } from 'react';
import FileUpload from './components/FileUpload';
import Summary from './components/Summary';
import TransactionList from './components/TransactionList';
import StatementAnalyser from './components/StatementAnalyser';
import API_BASE from './api';

function App() {
  const [activeTab, setActiveTab] = useState('categorizer');
  const [allTransactions, setAllTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const nextTxnId = useRef(1);

  const handleTransactionsUpload = (transactions, cardName, processedFiles = []) => {
    setError(null);
    if (processedFiles.length > 0) {
      setSuccess(`Successfully uploaded ${transactions.length} transactions from ${processedFiles.length} file(s)`);
    } else {
      setSuccess(`Successfully uploaded ${transactions.length} transactions from ${cardName}`);
    }
    
    // Combine with existing transactions
    const txWithIds = transactions.map((txn) => ({
      ...txn,
      _id: nextTxnId.current++,
    }));
    const combined = [...allTransactions, ...txWithIds];
    setAllTransactions(combined);
    
    // Auto-summarize
    setTimeout(() => {
      handleSummarize(combined);
    }, 500);
  };

  const handleSummarize = async (transactions = null) => {
    const txToSummarize = transactions || allTransactions;
    
    if (txToSummarize.length === 0) {
      setError('Please upload at least one file first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/summarize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ transactions: txToSummarize }),
      });

      if (!response.ok) {
        throw new Error('Failed to summarize expenses');
      }

      const data = await response.json();
      setSummary(data);
    } catch (err) {
      setError(err.message || 'Error summarizing data');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setAllTransactions([]);
    setSummary(null);
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="container">
      <div className="header">
        <h1>� Finance Manager</h1>
        <p>Analyze your credit card statements and categorize expenses</p>
      </div>

      <div className="tabs-navigation">
        <button 
          className={`tab-button ${activeTab === 'categorizer' ? 'active' : ''}`}
          onClick={() => setActiveTab('categorizer')}
        >
          💳 Credit Card Categorizer
        </button>
        <button 
          className={`tab-button ${activeTab === 'analyser' ? 'active' : ''}`}
          onClick={() => setActiveTab('analyser')}
        >
          📊 Statement Analyser
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {activeTab === 'categorizer' && (
        <div className="main-content">
          <FileUpload onTransactionsUpload={handleTransactionsUpload} />
          
          <div className="card">
            <h2>Actions</h2>
            <button onClick={() => handleSummarize()} disabled={loading}>
              {loading ? '⏳ Summarizing...' : '📊 Summarize All'}
            </button>
            <button onClick={handleClear} style={{ marginTop: '10px', background: '#dc3545' }}>
              🗑️ Clear All
            </button>
            {allTransactions.length > 0 && (
              <p style={{ marginTop: '15px', color: '#666' }}>
                <strong>{allTransactions.length}</strong> total transactions loaded
              </p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'categorizer' && summary && <Summary summary={summary} />}
      
      {activeTab === 'categorizer' && allTransactions.length > 0 && (
        <TransactionList transactions={allTransactions} />
      )}

      {activeTab === 'analyser' && (
        <StatementAnalyser setError={setError} setSuccess={setSuccess} />
      )}
    </div>
  );
}

export default App;
